(ns ductile.ui.workflow-visualization
  (:require ["scroll-into-view-if-needed" :as scrollIntoView]
            [clojure.string :as str]
            [ductile.service.label :refer [service-label]]
            [ductile.ui.components :as ui]
            [ductile.ui.components.omnibox :as omnibox]
            [ductile.ui.components.table :as table]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.fahrzeug :as fahrzeug]
            [ductile.ui.locale :as locale :refer [tr]]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.pred-view :as pred-view]
            [ductile.ui.util :as util]
            [ductile.ui.workflow :as workflow]
            [nextjournal.clerk.render.hooks :as hooks]
            [reagent.core :as r]))

(def triangle
  [:svg {:width "15", :height "15", :viewBbox "0 0 15 15", :fill "none", :xmlns "http://www.w3.org/2000/svg"}
   [:path {:d "M6 11L6 4L10.5 7.5L6 11Z", :fill "currentColor"}]])

(def !view-mode
  (deframe/!route-get :preds/mode #{:mode/explained}))

(def !view-preds
  (deframe/!route-get :preds/view #{}))

(def !preview-service
  (deframe/!route-get :preview-service nil))

(def !hover-preview-service
  (deframe/!route-get :hover-preview-service nil))

(defn render-preds [pred]
  [:div.flex
   [:div.flex.flex-col.gap-1
    (when (= @!view-mode #{:mode/explained :mode/code})
      {:class "bg-indigo-50/50 p-1 rounded-lg border border-indigo-100 shadow"})

    (when (@!view-mode :mode/code)
      [:div.flex
       (when (@!view-mode :mode/explained)
         {:class "opacity-60"})
       [pred-view/badge {:color :blue} (pr-str pred)]])
    (when (@!view-mode :mode/explained)
      [:div.flex.flex-wrap.items-center.gap-2.place-items-start
       (if (nil? pred)
         (pred-view/explain-pred (tr "Always"))
         (pred-view/explain-pred pred))])]])


(defn render-service [{:service/keys [code preds services]}]
  (r/with-let [!expanded? (r/atom true)]
    [:<>
     (when code
       [:li.flex.mt-1
        (when-not services
          [:div.ml-4])
        [:div
         (when preds
           (into [:ul.text-xs.flex.flex-wrap.items-center.relative
                  {:style {:z-index 1}}]
                 (map render-preds)
                 preds))
         [:div.flex.relative.ml-1 {:style {:margin-top -3 :z-index 0}}
          [:div.text-sm.px-3.py-1.rounded-full.border.flex.gap-1.text-black
           (cond-> {:class (concat (if @!expanded?
                                     ["bg-mauve2" "border-mauve3"]
                                     ["bg-mauve3" "border-mauve4"])
                                   (when services
                                     ["hover:bg-mauve3" "hover:border-mauve4" "cursor-pointer" "transition-all"]))}
             services (assoc :on-click #(swap! !expanded? not)))
           (when services
             [:div.text-black.mr-1 {:style {:margin-top 3}}
              [:div.origin-center.transition-all
               {:class (when @!expanded? "rotate-90")}
               triangle]])
           code]]]])
     (when @!expanded?
       [:div.relative
        (into [:ul.list-none.flex.flex-col.gap-1.ml-5]
              (map (fn [s] [render-service s]))
              services)
        (when (and (seq services) code)
          [:div.border-l.absolute {:style {:left 26 :top -15 :bottom 0}}])])]))

(defn render-workflow [workflow-name workflow]
  [:div.not-prose.mb-12
   [:h4 {:id workflow-name} workflow-name]
   [:div.-ml-5
    [render-service workflow]]])

(defn render-view [_result _match]
  (let [workflows (sort-by key (get-in @deframe/!current-route-fetch [:result :workflows]))]
    [ui/panel
     [ui/panel-sidebar
      [:div.sticky {:class "top-6"}
       [:h4.mb-3.text-base (tr "Overview")]
       (into [:div.text-xs.flex.flex-col.gap-2]
             (map (fn [[workflow-name _]]
                    [:div
                     [ui/link
                      {:on-click (fn [event]
                                   (.preventDefault event)
                                   (.stopPropagation event)
                                   (when-let [el (js/document.getElementById workflow-name)]
                                     (scrollIntoView el #js {:behavior "smooth" :block "start"})))}
                      workflow-name]]))
             workflows)
       [:h5.mb-3.text-sm (tr "View")]
       [:ul.text-xs.flex.flex-col.gap-1
        [:li [:label.text-indigo-700
              [:input {:type :radio
                       :checked (= #{:mode/code} @!view-mode)
                       :on-change #(deframe/route-assoc! :preds/mode #{:mode/code})}]
              (tr "Code")]]
        [:li [:label.text-indigo-700
              [:input {:type :radio
                       :checked (= #{:mode/explained} @!view-mode)
                       :on-change #(deframe/route-assoc! :preds/mode #{:mode/explained})}]
              (tr "Explained")]]
        [:li [:label.text-indigo-700
              [:input {:type :radio
                       :checked (= #{:mode/code :mode/explained} @!view-mode)
                       :on-change #(deframe/route-assoc! :preds/mode #{:mode/code :mode/explained})}]
              (tr "Both")]]]]]
     [ui/panel-content
      (into [:div
             [:h2.mb-6 (tr "Workflows")]]
            (map (fn [[workflow-name workflow]]
                   [render-workflow workflow-name workflow]))
            workflows)]]))


(defn dashed [title & children]
  [:div.border.border-dashed.border-slate-500.rounded-md.flex.flex-col.justify-between.items-center
   {:class "hover:bg-indigo-300/10"}
   (into [:div.p-1] children)
   [:div.text-center.px-1 {:class "text-[10px] opacity-80"} title]])

(defn service-cell
  ([service] [service-cell nil service])
  ([{:keys [plain?] :as opts} service]
   (let [operational-preds? (contains? @!view-preds :service/operational-preds)
         vehicle-preds? (contains? @!view-preds :service/vehicle-preds)
         previewing? (and (not plain?) (= @!preview-service service))]
     [:div.flex.flex-col.gap-2.p-2.rounded-lg
      {:data-testid "service-cell"
       :class [(when-not plain? "border border-indigo-300 shadow cursor-pointer active:bg-indigo-100")
               (if previewing? "bg-indigo-50" "hover:bg-indigo-50/40 hover:border-indigo-400")]
       :on-click (when-not plain? #(deframe/route-assoc! :preview-service (if previewing? nil service)))
       :on-mouse-enter (when-not plain? #(deframe/route-assoc! :hover-preview-service service))
       :on-mouse-leave #(when (and (not plain?) @!preview-service) (deframe/route-dissoc! :hover-preview-service))}

      (when (some (comp seq service) [:service/vehicle-preds :service/contract-preds :service/operational-preds])
        [:div.flex.gap-3.items-center.flex-wrap.items-stretch.empty:hidden
         (when vehicle-preds?
           [dashed (tr "Vehicle") [render-preds (:service/vehicle-preds service)]])
         (if (or operational-preds? vehicle-preds?)
           [dashed (tr "Contract") [render-preds (:service/contract-preds service)]]
           [:div.flex.items-center [render-preds (:service/contract-preds service)]])
         (when operational-preds?
           [dashed (tr "Operational") [render-preds (:service/operational-preds service)]])])

      [:span.font-bold.px-3.text-indigo-800
       (service-label service)]

      (when (:edi/orderable? service)
        [:div.flex [ui/badge {:variant :blue} (tr :edi/orderable?)]])

      (when-let [{:keys [at-entry? ->cycle-start cycle]} (:service/schedule service)]
        [:div.flex.items-center.text-indigo-600.px-3
         {:class ["text-[12px]" "-mt-2" "gap-[3px]"]}
         [ui/icon {:size 12} :clock]
         (str/join (str " " (tr "and") " ")
                   (concat
                    (when at-entry? [(tr "during entry")])
                    [(str (tr "every") " " (first cycle) " " (tr (name (second cycle))) " " (tr "after") " " (tr (or (:form ->cycle-start) ->cycle-start)))]))])

      (when-let [followup-services (not-empty (filter :service/code (:service/consequences service)))]
        (into [:div.ml-3 [:div.text-xs.font-bold.my-1 (tr :service/consequences)]]
              (map (fn [service]
                     [service-cell opts service]))
              followup-services))])))

(defn filter-services [filters services]
  (let [filter-fns (->> filters
                        (keep (fn [[path filter-val]]
                                (cond
                                  (= :filter/omnibox (:filter (meta filter-val)))
                                  #(omnibox/matches? (get-in % path) filter-val)

                                  :else
                                  (when-not (empty? filter-val)
                                    #(contains? filter-val (get-in % path)))))))]

    (cond->> services
      (not-empty filter-fns)
      (filterv (apply every-pred filter-fns)))))


(defn contract-services-table [services]
  (let [!expanded-at (hooks/use-state {})
        filtered-services (filter-services @(deframe/!route-get ::filters) services)]
    (if (empty? services)
      [:div.text-sm (tr "None")]
      [table/table {:column-order [:service/code
                                   :billing/revenue-account
                                   :billing/cost-center
                                   :billing/price]

                    :select-columns [:service/code
                                     :billing/revenue-account
                                     :billing/cost-center
                                     :billing/price]
                    :!expanded-at !expanded-at
                    :update-filters! (fn [k filter selected]
                                       (deframe/tx! [[:op/assoc-in [:current-route/transient ::filters (if (vector? k) k [k])] selected]]))

                    :filters (into {}
                                   (for [[k opts] [[:service/code]
                                                   [:billing/revenue-account]
                                                   [:billing/cost-center {:numeric? true}]
                                                   [:billing/price {:numeric? true}]]]
                                     [k [:omnibox
                                         (into (sorted-set)
                                               (map (if (vector? k) #(get-in % k) #(get % k)))
                                               services)
                                         opts]]))

                    :transform-columns {[:service/code]
                                        (fn [service] [service-cell {:plain? true} service])
                                        [:billing/revenue-account]
                                        (fn [service] [:div.tabular-nums.text-right (:billing/revenue-account service)])
                                        [:billing/price]
                                        (fn [service] [:div.tabular-nums.text-right (locale/format-number (merge {:maximumFractionDigits 10
                                                                                                                  :minimumFractionDigits 2}) (:billing/price service))])}}
       filtered-services])))

;; TODO: Billing-related keys are very rudimentary for now.
;; Match against `contract/render-services-table` to fix.

(defn show-contract [contract]
  [:div {:id (str/replace (:contract/name contract) #"\s" "")}
   [:div.not-prose.border.rounded.max-w-4xl.mx-auto.p-6.m-6.shadow
    [:div.text-lg.font-bold.text-slate-500 (tr "Contract")]
    [:h1.text-3xl.font-bold.mt-0 {:data-testid "contract"} (:contract/name contract)]
    [:div.border-t-2.border-slate-200.mt-3]
    [:div.text-sm.font-bold.mt-3.text-slate-500 "Fahrzeugattribute"]
    [fahrzeug/entity-view {} (:fahrzeug/attributes contract)]

    [:div.text-sm.font-bold.mt-6.text-slate-500 "Dienstleistungen"]
    (into
     [:div.overflow-auto.flex.flex-col.gap-2]
     (map service-cell)
     (:contract/services contract))

    [:div.text-sm.font-bold.mt-6.text-slate-500 "Bestellbare Dienstleistungen"]
    (into
     [:div.overflow-auto.flex.flex-col.gap-2]
     (map service-cell)
     (:contract/extra-services contract))]])

(defn workflow-contracts [contracts _match]
  [:div.mx-auto {:class "max-w-[1400px]"}
   [ui/panel {:variant :full}
    [ui/panel-sidebar
     [:div.sticky {:class "top-6"}
      [:h4.mb-3.text-base (tr "Contracts")]
      (into [:div]
            (map (fn [[parent-contract contracts]]
                   (into [:div.text-xs.flex.flex-col.gap-2 [:span.font-bold.mt-3 parent-contract]]
                         (map (fn [contract]
                                [(if parent-contract :div.ml-3 :div)
                                 [ui/link
                                  {:on-click (fn [event]
                                               (.preventDefault event)
                                               (.stopPropagation event)
                                               (when-let [el (js/document.getElementById (str/replace (:contract/name contract) #"\s" ""))]
                                                 (scrollIntoView el #js {:behavior "smooth" :block "start"})))}
                                  (:contract/name contract)]]))
                         (sort-by :contract/name contracts))))
            (group-by :contract/parent-contract contracts))

      [:h5.mb-3.text-sm (tr "Conditions")]
      [:div.flex.flex-col.gap-1
       [:ul.text-xs.flex.flex-col.gap-1
        [:li [:label.text-indigo-700
              [:input.mr-1 {:type :checkbox
                            :checked (contains? @!view-preds :service/vehicle-preds)
                            :on-change #(deframe/route-assoc! :preds/view (-> @!view-preds set ((if (.. % -target -checked) conj disj) :service/vehicle-preds)))}]
              (tr "Vehicle")]]]
       [:ul.text-xs.flex.flex-col.gap-1
        [:li [:label.text-indigo-700
              [:input.mr-1 {:type :checkbox
                            :checked (contains? @!view-preds :service/operational-preds)
                            :on-change #(deframe/route-assoc! :preds/view (-> @!view-preds set ((if (.. % -target -checked) conj disj) :service/operational-preds)))}]
              (tr "Operational")]]]]

      [:h5.mb-3.text-sm (tr "View")]
      [:ul.text-xs.flex.flex-col.gap-1
       [:li [:label.text-indigo-700
             [:input.mr-1 {:type :radio
                           :checked (= #{:mode/code} @!view-mode)
                           :on-change #(deframe/route-assoc! :preds/mode #{:mode/code})}]
             (tr "Code")]]
       [:li [:label.text-indigo-700
             [:input.mr-1 {:type :radio
                           :checked (= #{:mode/explained} @!view-mode)
                           :on-change #(deframe/route-assoc! :preds/mode #{:mode/explained})}]
             (tr "Explained")]]
       [:li [:label.text-indigo-700
             [:input.mr-1 {:type :radio
                           :checked (= #{:mode/code :mode/explained} @!view-mode)
                           :on-change #(deframe/route-assoc! :preds/mode #{:mode/code :mode/explained})}]
             (tr "Both")]]]]]
    [ui/panel-content
     (into [:div
            [:h2.mb-6 (tr "Workflows")]]
           (map show-contract)
           (sort-by (juxt :contract/parent-contract :contract/name) contracts))]

    (let [service (or @!hover-preview-service @!preview-service)]
      [:div.bg-white
       [:div.sticky.top-0.p-8.pl-2.h-dvh.overflow-auto
        [:> ui/history-context.Provider {:value true}
         [mobile/iphone-portrait
          [workflow/record-attrs-view {:compound/name (util/current-compound-name)} {:service service}]]]]])]])
